import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageHeader from '../components/common/PageHeader';
import NmapXmlResultView from '../components/workbench/NmapXmlResultView';
import { parseNmapXml, NmapParseResult } from '../utils/nmapXmlParser';

const WorkbenchNmapXmlPage: React.FC = () => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputTab, setInputTab] = useState(0);
  const [xmlText, setXmlText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<NmapParseResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processXml = useCallback((xml: string) => {
    setError(null);
    try {
      const parsed = parseNmapXml(xml);
      setResult(parsed);
      setXmlText(xml);
    } catch (e) {
      setError(`Fehler beim Parsen: ${e}`);
      setResult(null);
    }
  }, []);

  const handleFileRead = useCallback(
    (file: File) => {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        processXml(text);
      };
      reader.onerror = () => setError('Datei konnte nicht gelesen werden.');
      reader.readAsText(file);
    },
    [processXml],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileRead(file);
    },
    [handleFileRead],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileRead(file);
      if (e.target) e.target.value = '';
    },
    [handleFileRead],
  );

  const handleTextParse = useCallback(() => {
    if (xmlText.trim()) {
      setFileName(null);
      processXml(xmlText);
    }
  }, [xmlText, processXml]);

  const handleReset = useCallback(() => {
    setXmlText('');
    setFileName(null);
    setResult(null);
    setError(null);
  }, []);

  return (
    <Box>
      <PageHeader
        title="Nmap XML Viewer"
        subtitle="Nmap XML-Ergebnisse importieren und übersichtlich darstellen"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'Nmap XML Viewer' },
        ]}
      />

      {/* Input section */}
      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Tabs value={inputTab} onChange={(_, v) => setInputTab(v)} sx={{ mb: 2 }}>
          <Tab label="Datei hochladen" />
          <Tab label="XML einfügen" />
        </Tabs>

        {inputTab === 0 && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              bgcolor: dragOver
                ? `${theme.palette.primary.main}10`
                : 'transparent',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: `${theme.palette.primary.main}08`,
              },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              {fileName ? fileName : 'Nmap XML-Datei hierher ziehen oder klicken'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              .xml, .nmap oder beliebige Textdateien
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,.nmap,.txt"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </Box>
        )}

        {inputTab === 1 && (
          <Box>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              placeholder={'<?xml version="1.0"?>\n<nmaprun scanner="nmap" ...>'}
              value={xmlText}
              onChange={(e) => setXmlText(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: '0.82rem',
                },
              }}
            />
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleTextParse}
                disabled={!xmlText.trim()}
              >
                Parsen
              </Button>
              {result && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleReset}
                >
                  Zurücksetzen
                </Button>
              )}
            </Box>
          </Box>
        )}

        {result && inputTab === 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleReset}
            >
              Zurücksetzen
            </Button>
          </Box>
        )}
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && <NmapXmlResultView result={result} />}
    </Box>
  );
};

export default WorkbenchNmapXmlPage;
